
import { Home, Calendar, Heart, User, Plus } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useBooking } from "@/contexts/BookingContext";
import { motion } from "framer-motion";

const BottomNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { startBooking } = useBooking();
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleBookingButtonClick = () => {
    startBooking();
    navigate('/booking');
  };

  // Animation variants for the active indicator
  const indicatorVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { duration: 0.2, ease: "easeOut" } }
  };

  // Main navigation button styles
  const navButtonClass = "flex flex-col items-center relative";
  const navIconContainer = "p-2 rounded-full transition-all";
  const navIconActiveClass = "text-chrona-accent";
  const navIconInactiveClass = "text-chrona-gray-300";
  const navTextClass = "text-xs mt-1";

  return (
    <motion.div 
      className="fixed bottom-0 left-0 right-0 h-[72px] backdrop-blur-xl bg-black/80 border-t border-white/10 flex items-center justify-around z-50 px-2 md:hidden shadow-2xl safe-area-bottom"
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <Link to="/" className={navButtonClass} aria-label="Home">
        <div className={`${navIconContainer} ${isActive('/') ? navIconActiveClass : navIconInactiveClass}`}>
          <Home size={22} />
          {isActive('/') && (
            <motion.div 
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-1.5 h-1.5 rounded-full bg-chrona-accent"
              initial="hidden"
              animate="visible"
              variants={indicatorVariants}
            />
          )}
        </div>
        <span className={`${navTextClass} ${isActive('/') ? navIconActiveClass : navIconInactiveClass}`}>Home</span>
      </Link>
      
      <Link to="/dashboard" className={navButtonClass} aria-label="Termine">
        <div className={`${navIconContainer} ${isActive('/dashboard') ? navIconActiveClass : navIconInactiveClass}`}>
          <Calendar size={22} />
          {isActive('/dashboard') && (
            <motion.div 
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-1.5 h-1.5 rounded-full bg-chrona-accent"
              initial="hidden"
              animate="visible"
              variants={indicatorVariants}
            />
          )}
        </div>
        <span className={`${navTextClass} ${isActive('/dashboard') ? navIconActiveClass : navIconInactiveClass}`}>Termine</span>
      </Link>
      
      {/* Center floating action button with better touch area and interaction */}
      <div className={`${navButtonClass} -mt-10 touch-manipulation`}>
        <Button 
          onClick={handleBookingButtonClick}
          className="h-16 w-16 rounded-full bg-chrona-accent hover:bg-chrona-accent-light flex items-center justify-center shadow-xl shadow-chrona-accent/20 hover:shadow-chrona-accent/40 transition-all duration-300 hover:scale-105 active:scale-95"
          aria-label="Neuen Termin buchen"
        >
          <Plus size={26} className="text-chrona-text" />
        </Button>
        <span className={`${navTextClass} text-chrona-gray-300 mt-2`}>Buchen</span>
      </div>
      
      <Link to="/favorites" className={navButtonClass} aria-label="Favoriten">
        <div className={`${navIconContainer} ${isActive('/favorites') ? navIconActiveClass : navIconInactiveClass}`}>
          <Heart size={22} />
          {isActive('/favorites') && (
            <motion.div 
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-1.5 h-1.5 rounded-full bg-chrona-accent"
              initial="hidden"
              animate="visible"
              variants={indicatorVariants}
            />
          )}
        </div>
        <span className={`${navTextClass} ${isActive('/favorites') ? navIconActiveClass : navIconInactiveClass}`}>Favoriten</span>
      </Link>
      
      <Link to="/profile" className={navButtonClass} aria-label="Profil">
        <div className={`${navIconContainer} ${isActive('/profile') ? navIconActiveClass : navIconInactiveClass}`}>
          <User size={22} />
          {isActive('/profile') && (
            <motion.div 
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-1.5 h-1.5 rounded-full bg-chrona-accent"
              initial="hidden"
              animate="visible"
              variants={indicatorVariants}
            />
          )}
        </div>
        <span className={`${navTextClass} ${isActive('/profile') ? navIconActiveClass : navIconInactiveClass}`}>Profil</span>
      </Link>
    </motion.div>
  );
};

export default BottomNavigation;
